import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { API_URL, ALTCHA_CHALLENGE_URL, ALTCHA_ENABLED, HIKE_PAGE_ONLY, LOGIN_ADMIN_ONLY } from '../config';
import Spinner from '../components/Spinner';
import DynamicForm from '../components/DynamicForm';
import 'altcha';

export default function GuestRegistrationPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [hike, setHike] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    data_nascita: '',
  });
 const [formResponses, setFormResponses] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [success, setSuccess] = useState(false);
    const [altchaPayload, setAltchaPayload] = useState(null);
    const [errors, setErrors] = useState({});
    const altchaRef = useRef(null);

  // Handle ALTCHA widget events for simple form
  useEffect(() => {
    if (!ALTCHA_ENABLED || hike?.has_registration_form) return;
    const widget = altchaRef.current;
    if (!widget) return;

    const handleStateChange = (ev) => {
      if (ev.detail) {
        if (ev.detail.state === 'verified') {
          setAltchaPayload(ev.detail.payload);
        } else if (ev.detail.state === 'error') {
          setAltchaPayload(null);
        } else if (ev.detail.state === 'unverified') {
          setAltchaPayload(null);
        }
      }
    };

    widget.addEventListener('statechange', handleStateChange);
    return () => widget.removeEventListener('statechange', handleStateChange);
  }, [hike]);

  const handleAltchaPayloadChange = (payload) => {
    setAltchaPayload(payload);
  };

  useEffect(() => {
    const fetchHike = async () => {
      try {
        const response = await fetch(`${API_URL}/hikes/${id}`);
        if (!response.ok) {
          throw new Error('Gita non trovata');
        }
        const data = await response.json();
        setHike(data);
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    fetchHike();
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value.replace(/[^0-9+]/g, '');
    setFormData(prev => ({ ...prev, telefono: value }));
    if (errors.telefono) {
      setErrors(prev => ({ ...prev, telefono: null }));
    }
  };

  const handleFormResponseChange = (fieldId, value) => {
    setFormResponses(prev => ({
      ...prev,
      [fieldId]: value
    }));
  };

const validateBasicFields = () => {
      const newErrors = {};
      
      if (!formData.nome) {
        newErrors.nome = 'Questo campo è obbligatorio';
      }
      if (!formData.cognome) {
        newErrors.cognome = 'Questo campo è obbligatorio';
      }
      if (!formData.email) {
        newErrors.email = 'Questo campo è obbligatorio';
      } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
        newErrors.email = 'Email non valida';
      }
      if (!formData.telefono) {
        newErrors.telefono = 'Questo campo è obbligatorio';
      }
      if (!formData.data_nascita) {
        newErrors.data_nascita = 'Questo campo è obbligatorio';
      }
      
      return newErrors;
    };

  const handleSubmit = async (formDataFromDynamicForm = null) => {
      // When called from DynamicForm, formDataFromDynamicForm contains the registration form responses
      // We need to merge it with the main form data
      const finalFormResponses = formDataFromDynamicForm || formResponses;
      
      // Validate ALTCHA (only if enabled)
      if (ALTCHA_ENABLED && !altchaPayload) {
        setError('Completa la verifica CAPTCHA');
        return;
      }
      
      setIsSubmitting(true);
      setError('');
     
     try {
       const response = await fetch(`${API_URL}/hikes/${id}/guest-register`, {
         method: 'POST',
         headers: {
           'Content-Type': 'application/json',
         },
         body: JSON.stringify({
           ...formData,
           form_responses: hike?.has_registration_form ? finalFormResponses : undefined,
           altcha_payload: altchaPayload,
         }),
       });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Errore durante la registrazione');
      }
      
      setSuccess(true);
      setIsSubmitting(false);
    } catch (err) {
      setError(err.message);
      setIsSubmitting(false);
    }
  };

 if (loading) {
     return (
       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
         <Spinner size="lg" />
       </div>
     );
   }

   if (success) {
     return (
       <div className="min-h-screen bg-gray-50 flex items-center justify-center">
         <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full mx-4 text-center">
           <h2 className="text-xl font-semibold text-green-600 mb-4">Iscrizione ricevuta!</h2>
           <p className="text-gray-700 mb-6">
              Ti abbiamo inviato un email di conferma all'indirizzo {formData.email}.
              Se la gita richiede approvazione, riceverai una notifica quando la tua iscrizione sarà confermata.
              Se non vedi la mail, controlla la cartella spam/indesiderata.
            </p>
           <button
             onClick={() => navigate('/')}
             className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 transition"
           >
             Torna alla homepage
           </button>
         </div>
       </div>
     );
   }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h1 className="text-2xl font-bold text-gray-800 mb-6">Iscrizione alla gita</h1>

           {hike && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">{hike.titolo}</h3>
              <p className="text-sm text-gray-600">{hike.data_inizio}</p>
              {hike.luogo && <p className="text-sm text-gray-600">{hike.luogo}</p>}
            </div>
          )}

          {hike?.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date() && (
            <div className="mb-6 p-4 bg-amber-50 border-l-4 border-amber-400 rounded-md">
              <p className="text-sm text-amber-800">
                Le iscrizioni per questa gita apriranno il {new Date(hike.data_apertura_iscrizioni).toLocaleDateString('it-IT', { day: 'numeric', month: 'long', year: 'numeric' })}.
              </p>
            </div>
          )}

           <form onSubmit={handleSubmit} className="space-y-4">
<div className="grid grid-cols-1 md:grid-cols-2 gap-4">
               <div>
                 <label htmlFor="nome" className="block text-sm font-medium text-gray-700 mb-1">Nome*</label>
                 <input
                   type="text"
                   id="nome"
                   name="nome"
                   value={formData.nome}
                   onChange={handleChange}
                   className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.nome ? 'border-red-500' : 'border-gray-300'}`}
                 />
                 {errors.nome && <p className="mt-1 text-sm text-red-600">{errors.nome}</p>}
               </div>
               <div>
                 <label htmlFor="cognome" className="block text-sm font-medium text-gray-700 mb-1">Cognome*</label>
                 <input
                   type="text"
                   id="cognome"
                   name="cognome"
                   value={formData.cognome}
                   onChange={handleChange}
                   className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.cognome ? 'border-red-500' : 'border-gray-300'}`}
                 />
                 {errors.cognome && <p className="mt-1 text-sm text-red-600">{errors.cognome}</p>}
               </div>
             </div>

<div>
               <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email*</label>
               <input
                 type="email"
                 id="email"
                 name="email"
                 value={formData.email}
                 onChange={handleChange}
                 className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
               />
               {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
             </div>

             <div>
               <label htmlFor="telefono" className="block text-sm font-medium text-gray-700 mb-1">Telefono*</label>
             <input
                  type="tel"
                  id="telefono"
                  name="telefono"
                  value={formData.telefono}
                  onChange={handlePhoneChange}
                  className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${errors.telefono ? 'border-red-500' : 'border-gray-300'}`}
                />
               {errors.telefono && <p className="mt-1 text-sm text-red-600">{errors.telefono}</p>}
             </div>

            <div>
              <label htmlFor="data_nascita" className="block text-sm font-medium text-gray-700 mb-1">Data di nascita*</label>
              <input
                type="date"
                id="data_nascita"
                name="data_nascita"
                value={formData.data_nascita}
                onChange={handleChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
         </div>

                {hike?.has_registration_form && hike?.registration_form_schema && (
                  <div className="border-t pt-6 mt-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-4">Modulo di iscrizione</h3>
                    {error && <div className="mb-4 p-3 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm">{error}</div>}
                    <DynamicForm
                      schema={hike.registration_form_schema}
                      values={formResponses}
                      onChange={handleFormResponseChange}
                      onSubmit={handleSubmit}
                      onCancel={() => navigate(-1)}
                      loading={isSubmitting || (hike?.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date())}
                      altchaEnabled={ALTCHA_ENABLED}
                      altchaChallengeUrl={ALTCHA_CHALLENGE_URL}
                      onAltchaPayloadChange={handleAltchaPayloadChange}
                      validateBasicFields={validateBasicFields}
                      onBasicFieldError={(basicErrors) => setErrors(basicErrors)}
                    />
                  </div>
                )}

              {!hike?.has_registration_form && (
                 <>
                   <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 rounded-md">
                     <p className="text-sm text-yellow-800">
                       <strong>Attenzione:</strong> Se la gita richiede approvazione da parte degli organizzatori, riceverai una notifica quando la tua iscrizione sarà confermata. Se non vedi la mail, controlla la cartella spam/indesiderata.
                     </p>
                   </div>

                 {error && <div className="p-3 bg-red-50 border-l-4 border-red-400 text-red-700 text-sm">{error}</div>}

                   {/* ALTCHA Widget - Self-hosted CAPTCHA */}
                   {ALTCHA_ENABLED && ALTCHA_CHALLENGE_URL && (
                     <div className="flex flex-col items-center gap-2">
                      <altcha-widget
                        ref={altchaRef}
                        challengeurl={ALTCHA_CHALLENGE_URL}
                        hidefooter
                        strings={JSON.stringify({
                          label: "Non sono un robot",
                          verifying: "Verifica in corso...",
                          verified: "Verificato",
                          error: "Verifica fallita. Riprova.",
                          expired: "Verifica scaduta. Riprova.",
                          waitAlert: "Verifica in corso... attendere."
                        })}
                      />
                    </div>
                  )}

 <button
                     type="submit"
                     disabled={isSubmitting || (hike?.data_apertura_iscrizioni && new Date(hike.data_apertura_iscrizioni) > new Date())}
                     className="w-full bg-blue-600 text-white py-3 px-4 rounded-md font-semibold hover:bg-blue-700 transition disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                   >
                     {isSubmitting ? <><Spinner size="sm" color="white" /> Caricamento...</> : 'Invia iscrizione'}
                   </button>
                </>
              )}

            {!HIKE_PAGE_ONLY && !LOGIN_ADMIN_ONLY && (
              <p className="text-xs text-gray-500 text-center mt-4">
                Hai già un account? <a href="/login" className="text-blue-600 underline">Accedi</a> per iscriverti con il tuo account.
              </p>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
